import axios from 'axios';

/**
 * Axios instance factory shared by web and admin.
 *
 * The factory owns the *mechanics* (instance creation, bearer attachment,
 * refresh plumbing); each app injects its own *policy*:
 *
 * - web: proactive refresh in the request interceptor (`proactiveRefresh`),
 *   anonymous-tracking headers via `onRequest`, public-path skip list.
 * - admin: reactive refresh in the response interceptor (`refreshOn401`)
 *   with retry-once and redirect-to-login on failure.
 *
 * Both refresh paths are expected to be single-flight — wrap the injected
 * `execute` with `createSingleFlight` so concurrent 401s/expiries share one
 * refresh call.
 *
 * @param {Object} options
 * @param {string} options.baseURL
 * @param {number} [options.timeout]
 * @param {Object} [options.headers] - Default headers (Content-Type json by default)
 * @param {Function} [options.getLanguage] - () => string|null; sent as
 *   `Accept-Language` on every request when truthy. This is the language the
 *   API writes its prose in (descriptions, explanations, error copy), so an
 *   app passes the language it *renders* — web its UI language, admin a fixed
 *   'en' — rather than letting the browser attach the OS language on its own.
 *   Read per request, so a language switch takes effect on the next call.
 * @param {Function} [options.onRequest] - async (config) => void, runs first on
 *   every request (e.g. attach anonymous-tracking headers)
 * @param {Function} [options.isPublicPath] - (url) => boolean; when true, no
 *   auth is attached and no refresh is attempted for that request
 * @param {Function} [options.getAccessToken] - () => string|null; attached as
 *   `Authorization: Bearer <token>` when truthy
 * @param {Object} [options.proactiveRefresh] - Web-style request-side refresh:
 *   { shouldRefresh: () => boolean, execute: async () => string (new access
 *   token), onFailure: (error) => void }. On failure the request continues
 *   WITHOUT auth (public/shared-link friendly), it is not rejected.
 * @param {Object} [options.refreshOn401] - Admin-style response-side refresh:
 *   { execute: async () => string (new access token), onFailure: (error) =>
 *   void }. Retries the original request once with the returned token; if
 *   refresh fails, onFailure runs and the original 401 is rejected. `execute`
 *   should also persist the new token where `getAccessToken` reads it so
 *   subsequent requests pick it up (both apps store to localStorage).
 * @returns {import('axios').AxiosInstance}
 */
export function createApiClient({
  baseURL,
  timeout,
  headers = { 'Content-Type': 'application/json' },
  getLanguage,
  onRequest,
  isPublicPath,
  getAccessToken,
  proactiveRefresh,
  refreshOn401,
}) {
  const instance = axios.create({
    baseURL,
    timeout,
    headers,
  });

  instance.interceptors.request.use(
    async (config) => {
      // A refreshOn401 retry already carries the refreshed Authorization
      // header (and the original request's other headers) — re-running the
      // pipeline would overwrite it with a re-read from storage.
      if (config._retry) {
        return config;
      }

      const language = getLanguage?.();
      if (language) {
        config.headers['Accept-Language'] = language;
      }

      if (onRequest) {
        await onRequest(config);
      }

      if (isPublicPath && isPublicPath(config.url)) {
        return config;
      }

      if (proactiveRefresh && proactiveRefresh.shouldRefresh()) {
        try {
          const newToken = await proactiveRefresh.execute();
          config.headers['Authorization'] = `Bearer ${newToken}`;
          return config;
        } catch (error) {
          // Continue unauthenticated: shared links must keep working when a
          // stale session can't be refreshed.
          proactiveRefresh.onFailure?.(error);
          return config;
        }
      }

      const token = getAccessToken?.();
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
      }

      return config;
    },
    (error) => Promise.reject(error)
  );

  if (refreshOn401) {
    instance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const newToken = await refreshOn401.execute();
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return instance(originalRequest);
          } catch (refreshError) {
            refreshOn401.onFailure?.(refreshError);
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  return instance;
}
